import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import {
    PlaneTakeoff, PlaneLanding, Palmtree,
    Ticket, Landmark, Trash2, Plus,
    Clock, ShieldCheck, AlertCircle,
    Plane, MoveRight, ChevronDown
} from "lucide-react";

// Function to create a fresh copy of empty flight
const createFreshFlight = (createEmptyFlight) => {
    const newFlight = createEmptyFlight();
    return { ...newFlight };
};

const FlightForm = ({
    flights,
    setFlights,
    createEmptyFlight,
    preview,
    setPreview,
    loading,
    errors,
    handleChange,
    onSave
}) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    const STATUS_CONFIG = {
        pending: { bg: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending Verification" },
        approve: { bg: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <ShieldCheck size={14} />, label: "Verified & Approved" },
        reject: { bg: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Information Rejected" },
    };

    // Ensure flights is always an array
    const safeFlights = Array.isArray(flights) ? flights : [];

    // Initialize expanded state for flight cards
    useEffect(() => {
        const initialExpandedState = {};
        safeFlights.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedCards(initialExpandedState);
    }, [safeFlights.length]);

    const toggleCard = (index) => {
        setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const addFlight = useCallback(() => {
        if (setFlights && createEmptyFlight) {
            setFlights(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newFlight = createFreshFlight(createEmptyFlight);
                const newArray = [...currentArray, newFlight];
                const newIndex = newArray.length - 1;

                setExpandedCards(prevExpanded => ({
                    ...prevExpanded,
                    [newIndex]: true
                }));

                return newArray;
            });
        }
    }, [setFlights, createEmptyFlight]);

    const removeFlight = useCallback((index) => {
        if (setFlights) {
            setFlights(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newArray = currentArray.filter((_, i) => i !== index);

                setExpandedCards(prevExpanded => {
                    const newExpanded = {};
                    Object.keys(prevExpanded).forEach(key => {
                        const numKey = parseInt(key);
                        if (numKey < index) {
                            newExpanded[numKey] = prevExpanded[numKey];
                        } else if (numKey > index) {
                            newExpanded[numKey - 1] = prevExpanded[numKey];
                        }
                    });
                    return newExpanded;
                });

                return newArray;
            });
        }
    }, [setFlights]);

    const changeField = useCallback((index, field, value) => {
        if (handleChange) {
            handleChange(index, field, value);
        }
    }, [handleChange]);

    const validateForm = () => {
        const newErrors = {};
        safeFlights.forEach((flight, i) => {
            if (!flight.fromAirport && flight.toAirport) {
                newErrors[`fromAirport_${i}`] = "Departure airport required";
            }
            if (flight.fromAirport && !flight.toAirport) {
                newErrors[`toAirport_${i}`] = "Arrival airport required";
            }
        });
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving flight records...</p>
                    </div>
                </div>
            )}

            {/* ERROR BOX */}
            {(Object.keys(localErrors).length > 0 || errors?.length > 0) && (
                <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                    <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                        <AlertCircle size={20} /> <span className="text-sm font-semibold">Please correct the following:</span>
                    </div>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                        {Object.values(localErrors).map((e, i) => (
                            <li key={i} className="text-rose-600 text-[11px] py-0.5">• {e}</li>
                        ))}
                        {errors?.map((e, i) => (
                            <li key={`err-${i}`} className="text-rose-600 text-[11px] py-0.5">• {e}</li>
                        ))}
                    </ul>
                </div>
            )}

            {preview && !isEditing ? (
                /* ================= PREVIEW SECTION ================= */
                <div className="space-y-6 animate-in fade-in duration-500">
                    {/* Status Banner */}
                    {preview.status === "approve" && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <ShieldCheck size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Flight itinerary verified and approved</p>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-100 shadow-sm">
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Flight Itinerary</h2>
                            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mt-1">Declared Air Travel History</p>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border font-semibold text-[9px] sm:text-[10px] uppercase tracking-wider ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.bg || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                            {STATUS_CONFIG[preview?.status?.toLowerCase()]?.icon}
                            <span>{STATUS_CONFIG[preview?.status?.toLowerCase()]?.label || preview?.status || "Pending"}</span>
                        </div>
                    </div>

                    <div className="grid gap-4">
                        {preview.flights?.map((f, i) => (
                            <div key={i} className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                                <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                        <span className="text-base sm:text-lg font-bold text-blue-600 uppercase">{f.fromAirport || "—"}</span>
                                        <MoveRight className="text-gray-300" size={16} />
                                        <span className="text-base sm:text-lg font-bold text-blue-600 uppercase">{f.toAirport || "—"}</span>
                                    </div>
                                    <span className="text-[9px] sm:text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Flight #{i + 1}</span>
                                </div>
                                <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                                    <PreviewItem label="Airline" value={f.airline} icon={<Plane size={14} />} />
                                    <PreviewItem label="Ticket Number" value={f.ticketNumber} icon={<Ticket size={14} />} />
                                    <PreviewItem label="Transit Point" value={f.transit} icon={<Palmtree size={14} />} />
                                    <PreviewItem label="Airport Charges" value={f.airportCharges ? `$${f.airportCharges}` : "—"} icon={<Landmark size={14} />} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {(preview.status === "reject" || !preview.status) && (
                        <div className="flex justify-center pt-4">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md flex items-center gap-2"
                            >
                                Update Information
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                /* ================= FORM SECTION ================= */
                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8 animate-in slide-in-from-bottom-8 duration-700">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="p-2 sm:p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl sm:rounded-2xl shadow-lg">
                            <Plane size={24} className="sm:w-[28px] sm:h-[28px]" />
                        </div>
                        <div>
                            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Flight Details</h1>
                            <p className="text-gray-400 text-[10px] sm:text-xs font-medium uppercase tracking-wider mt-1">Provide accurate ticket information</p>
                        </div>
                    </div>

                    <div className="space-y-6 sm:space-y-8">
                        {safeFlights.length === 0 ? (
                            <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                <Plane size={40} className="text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">No flight records added</p>
                                <button
                                    type="button"
                                    onClick={addFlight}
                                    className="mt-3 text-blue-600 text-sm font-medium"
                                >
                                    + Add your first flight
                                </button>
                            </div>
                        ) : (
                            safeFlights.map((flight, index) => (
                                <div key={index} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                                    {/* Card Header - Clickable on mobile */}
                                    <div
                                        className="flex justify-between items-center p-4 sm:p-5 md:p-6 cursor-pointer md:cursor-default bg-gray-50/50"
                                        onClick={() => toggleCard(index)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                                {index + 1}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                                                    {flight.fromAirport && flight.toAirport
                                                        ? `${flight.fromAirport} → ${flight.toAirport}`
                                                        : "New Flight Entry"}
                                                </h3>
                                                {flight.airline && (
                                                    <p className="text-[10px] sm:text-xs text-gray-500">{flight.airline}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                className="md:hidden text-gray-400"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleCard(index);
                                                }}
                                            >
                                                <ChevronDown size={18} className={`transition-transform ${expandedCards[index] ? 'rotate-180' : ''}`} />
                                            </button>
                                            {safeFlights.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeFlight(index);
                                                    }}
                                                    className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Card Content - Expandable on mobile */}
                                    <div className={`
                                        transition-all duration-300 overflow-hidden
                                        ${expandedCards[index] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                                    `}>
                                        <div className="p-4 sm:p-5 md:p-6 pt-0 sm:pt-0 border-t border-gray-100">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                                <Input
                                                    label="From Airport"
                                                    icon={<PlaneTakeoff size={14} />}
                                                    placeholder="e.g., DEL, JFK, LHR"
                                                    value={flight.fromAirport}
                                                    onChange={(v) => changeField(index, "fromAirport", v)}
                                                    error={localErrors[`fromAirport_${index}`]}
                                                />
                                                <Input
                                                    label="To Airport"
                                                    icon={<PlaneLanding size={14} />}
                                                    placeholder="e.g., YYZ, SYD, DXB"
                                                    value={flight.toAirport}
                                                    onChange={(v) => changeField(index, "toAirport", v)}
                                                    error={localErrors[`toAirport_${index}`]}
                                                />
                                                <Input
                                                    label="Airline"
                                                    icon={<Plane size={14} />}
                                                    placeholder="e.g., Emirates, Air Canada"
                                                    value={flight.airline}
                                                    onChange={(v) => changeField(index, "airline", v)}
                                                />
                                                <Input
                                                    label="Ticket Number"
                                                    icon={<Ticket size={14} />}
                                                    placeholder="e.g., 1234567890"
                                                    value={flight.ticketNumber}
                                                    onChange={(v) => changeField(index, "ticketNumber", v)}
                                                />
                                                <Input
                                                    label="Transit Point"
                                                    icon={<Palmtree size={14} />}
                                                    placeholder="e.g., Dubai, London"
                                                    value={flight.transit}
                                                    onChange={(v) => changeField(index, "transit", v)}
                                                />
                                                <Input
                                                    type="number"
                                                    label="Airport Charges (CAD)"
                                                    icon={<Landmark size={14} />}
                                                    placeholder="0.00"
                                                    value={flight.airportCharges}
                                                    onChange={(v) => changeField(index, "airportCharges", v)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            type="button"
                            onClick={addFlight}
                            className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 py-3 rounded-xl font-semibold text-sm transition-all"
                        >
                            <Plus size={18} /> Add Another Flight
                        </button>

                    </div>
                </form>
            )}
        </div>
    );
};

/* HELPER COMPONENTS */
const Input = ({ label, value, onChange, type = "text", icon, placeholder, error }) => (
    <div className="space-y-1.5">
        <label className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wider ml-1 flex items-center gap-2">
            {icon} {label}
        </label>
        <input
            type={type}
            value={value || ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm placeholder:text-gray-400
                ${error ? "border-red-400 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
        />
        {error && <p className="text-rose-500 text-[10px] mt-1">{error}</p>}
    </div>
);

const PreviewItem = ({ label, value, icon }) => (
    <div className="flex gap-3">
        <div className="mt-1 h-8 w-8 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400 shrink-0">
            {icon}
        </div>
        <div className="overflow-hidden">
            <span className="text-[9px] sm:text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-0.5">
                {label}
            </span>
            <p className="font-semibold text-gray-800 text-sm leading-tight truncate">
                {value || "—"}
            </p>
        </div>
    </div>
);

export default FlightForm;